// lib/utils/imageUtils.ts

/**
 * 対応している画像形式をチェック
 */
export const isSupportedImageType = (type: string): boolean => {
  const supportedTypes = ["image/png", "image/jpeg", "image/gif", "image/webp"];
  return supportedTypes.includes(type);
};

/**
 * ファイルサイズをチェック（5MB上限）
 */
export const isValidFileSize = (file: File): boolean => {
  const maxSize = 5 * 1024 * 1024; // 5MB
  return file.size <= maxSize;
};

/**
 * 画像をリサイズしてBase64に変換
 */
export const resizeImageToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      reject(new Error("Canvas context not available"));
      return;
    }

    img.onload = () => {
      // 元の画像サイズ
      const { width, height } = img;

      // リサイズ計算（最大幅800px）
      let newWidth = width;
      let newHeight = height;

      if (width > 800) {
        newWidth = 800;
        newHeight = (height * 800) / width;
      }

      // Canvasにリサイズして描画
      canvas.width = newWidth;
      canvas.height = newHeight;
      ctx.drawImage(img, 0, 0, newWidth, newHeight);

      // Base64に変換（JPEG、圧縮率0.8）
      const base64 = canvas.toDataURL("image/jpeg", 0.8);
      resolve(base64);
    };

    img.onerror = () => {
      reject(new Error("Failed to load image"));
    };

    // FileをData URLとして読み込み
    const reader = new FileReader();
    reader.onload = (e) => {
      img.src = e.target?.result as string;
    };
    reader.onerror = () => {
      reject(new Error("Failed to read file"));
    };
    reader.readAsDataURL(file);
  });
};

/**
 * クリップボードから画像ファイルを取得
 */
export const getImageFromClipboard = (event: ClipboardEvent): File | null => {
  const items = event.clipboardData?.items;
  if (!items) return null;

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.type.startsWith("image/")) {
      const file = item.getAsFile();
      return file;
    }
  }
  return null;
};

/**
 * カーソル位置に画像要素を挿入
 */
export const insertImageAtCursor = (
  base64: string,
  alt = "Pasted image",
): void => {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return;

  const range = selection.getRangeAt(0);

  // 画像要素を作成
  const img = document.createElement("img");
  img.src = base64;
  img.alt = alt;
  img.style.maxWidth = "100%";
  img.style.height = "auto";
  img.style.display = "inline-block";
  img.style.verticalAlign = "middle";
  img.style.margin = "0 4px";

  // カーソル位置に挿入
  range.deleteContents();
  range.insertNode(img);

  // カーソルを画像の後ろに移動
  range.setStartAfter(img);
  range.setEndAfter(img);
  selection.removeAllRanges();
  selection.addRange(range);
};
